import { ComponentType, SVGProps } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Monitor, Smartphone, Tablet, Calendar, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { useDeleteUserSessionMutation } from '@/features/user-profile/hooks/mutations/use-delete-user-session.ts';

type DeviceType = 'Desktop' | 'Tablet' | 'Mobile' | 'Unknown';
type BrowserType = 'Chrome' | 'Firefox' | 'Safari' | 'Edge' | 'Unknown';
type OSType = 'Windows' | 'macOS' | 'Linux' | 'Android' | 'iOS' | 'Unknown';

type UAParsed = {
  device: DeviceType;
  browser: BrowserType;
  os: OSType;
  DeviceIcon: ComponentType<SVGProps<SVGSVGElement>>;
};

type Session = {
  sessionId: string;
  userAgent?: string;
  isCurrentSession: boolean;
  createdAt?: string;
};

type CircleMarkProps = {
  label: string;
  title?: string;
  className?: string;
  bgClass?: string;
  textClass?: string;
};

const CircleMark = ({
  label,
  title,
  className = '',
  bgClass = 'bg-muted',
  textClass = 'text-white',
}: CircleMarkProps) => (
  <span
    title={title}
    className={`inline-flex items-center justify-center rounded-full font-semibold ${bgClass} ${textClass} ${className}`}
  >
    {label}
  </span>
);

const parseUserAgent = (userAgent?: string): UAParsed => {
  if (!userAgent)
    return {
      device: 'Unknown',
      browser: 'Unknown',
      os: 'Unknown',
      DeviceIcon: Monitor,
    };

  const isTablet = /iPad|Tablet/.test(userAgent);
  const isMobile = /Mobile|Android|iPhone/.test(userAgent) && !isTablet;

  let device: DeviceType = 'Desktop';
  let DeviceIcon = Monitor;
  if (isTablet) {
    device = 'Tablet';
    DeviceIcon = Tablet;
  } else if (isMobile) {
    device = 'Mobile';
    DeviceIcon = Smartphone;
  }

  const browser: BrowserType = userAgent.includes('Chrome')
    ? 'Chrome'
    : userAgent.includes('Firefox')
      ? 'Firefox'
      : userAgent.includes('Safari') && !userAgent.includes('Chrome')
        ? 'Safari'
        : userAgent.includes('Edg') || userAgent.includes('Edge')
          ? 'Edge'
          : 'Unknown';

  const os: OSType = userAgent.includes('Windows')
    ? 'Windows'
    : userAgent.includes('Mac')
      ? 'macOS'
      : userAgent.includes('Linux')
        ? 'Linux'
        : userAgent.includes('Android')
          ? 'Android'
          : userAgent.includes('iPhone') ||
              userAgent.includes('iPad') ||
              userAgent.includes('iOS')
            ? 'iOS'
            : 'Unknown';

  return { device, browser, os, DeviceIcon };
};

const browserStyle = (browser: BrowserType) => {
  switch (browser) {
    case 'Chrome':
      return { label: 'Ch', bg: 'bg-blue-500' };
    case 'Firefox':
      return { label: 'Fx', bg: 'bg-orange-500' };
    case 'Safari':
      return { label: 'Sf', bg: 'bg-cyan-500' };
    case 'Edge':
      return { label: 'Ed', bg: 'bg-green-500' };
    default:
      return { label: '?', bg: 'bg-muted' };
  }
};

const osStyle = (os: OSType) => {
  switch (os) {
    case 'Windows':
      return { label: 'Win', bg: 'bg-blue-600' };
    case 'macOS':
      return { label: '', bg: 'bg-neutral-900', text: 'text-white' };
    case 'Linux':
      return { label: 'Lin', bg: 'bg-slate-700' };
    case 'Android':
      return { label: 'And', bg: 'bg-emerald-600' };
    case 'iOS':
      return { label: 'iOS', bg: 'bg-neutral-800' };
    default:
      return { label: '?', bg: 'bg-muted' };
  }
};

type ActiveSessionItemProps = {
  session: Session;
};

export function ActiveSessionItem({ session }: ActiveSessionItemProps) {
  const { mutate: deleteUserSession, isPending } = useDeleteUserSessionMutation();

  const handleTerminateSession = async (sessionId: string) => {
    deleteUserSession({ sessionId });
  };

  const { device, browser, os, DeviceIcon } = parseUserAgent(session.userAgent);
  const isCurrent = session.isCurrentSession;
  const signedInAt = session.createdAt ? new Date(session.createdAt) : null;

  const b = browserStyle(browser);
  const o = osStyle(os);

  return (
    <div
      className={[
        'flex items-center justify-between rounded-lg border p-4 transition-colors',
        isCurrent ? 'ring-primary/40 bg-primary/5 ring-1' : 'hover:bg-muted/30',
      ].join(' ')}
    >
      <div className="flex min-w-0 items-start gap-4">
        <div className="bg-muted relative flex h-12 w-12 shrink-0 items-center justify-center rounded-full">
          <DeviceIcon className="text-foreground/80 h-6 w-6" />
        </div>

        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate font-medium">
              {device}
              {os !== 'Unknown' ? ` • ${os}` : ''}
            </p>
            {isCurrent && <Badge className="text-xs">Current</Badge>}
          </div>

          <div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
            <div className="flex items-center gap-1.5">
              <CircleMark
                label={b.label}
                title={browser}
                bgClass={b.bg}
                className="h-4 w-4 text-[8px]"
              />
              <span className="truncate">{browser}</span>
            </div>

            {os !== 'Unknown' && (
              <div className="flex items-center gap-1.5">
                <CircleMark
                  label={o.label}
                  title={os}
                  bgClass={o.bg}
                  textClass={o.text ?? 'text-white'}
                  className="h-4 w-4 text-[8px]"
                />
                <span className="truncate">{os}</span>
              </div>
            )}
          </div>

          <div className="text-muted-foreground mt-2 flex flex-wrap items-center gap-4 text-xs">
            {signedInAt && (
              <div className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" />
                <span>Signed in {format(signedInAt, "MMM d, yyyy 'at' HH:mm")}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {!isCurrent && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleTerminateSession(session.sessionId)}
          disabled={isPending}
          className="text-destructive hover:text-destructive"
          aria-label="Terminate this session"
        >
          {isPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Terminating…
            </>
          ) : (
            <>
              <Trash2 className="mr-1 h-4 w-4" />
              <span className="hidden sm:block">Terminate</span>
            </>
          )}
        </Button>
      )}
    </div>
  );
}
